import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  ChevronUp,
  ChevronDown,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

import { ACCENT_ON_LIGHT } from "../../theme/accent";

const SUBCOPY =
  "Our Web3 focussed services for Advisory, Marketing, Events & Media";

const SERVICES = [
  {
    number: "01",
    label: "Advisory",
    title: "Growth & Market Readiness",
    description:
      "Our advisory services focus on the growth & market readiness for early stage projects from ideation to Token launch, Exchange Listings & Market Making. Over the years we have helped projects in their tokenomics, business planning & go to market strategies.",
    callout: "Ideation → token launch → listings.",
  },
  {
    number: "02",
    label: "Marketing",
    title: "Built for Cryptos & Web3",
    description:
      "We understand the nuances and the challenges that Web3 projects face in this constantly evolving & nascent space. Our customised marketing approach focusses on growth & brand building with our internal expertise and through our partners including leading media & KOLs.",
    callout: "Media & KOL distribution.",
  },
  {
    number: "03",
    label: "Events",
    title: "Meetups, Demo Days & Summits",
    description:
      "We understand the power of engagement and community building and have been partnering with the top Crypto & Web3 events worldwide and creating curated events, meetups & demo days bringing together founders, investors & our community.",
    callout: "Curated events worldwide.",
  },
  {
    number: "04",
    label: "Media",
    title: "Podcast, Blog & Newsletter",
    description:
      "A robust media strategy is the lifeline of brand building & community engagement, and we are creating a premier media channel with a Podcast, Blog & Newsletter with news & stories of founders, KOLs and the markets to share with our growing global community of Crypto & Web3 enthusiasts.",
    callout: "Podcast · Blog · Newsletter.",
  },
];

// This section is `bg-white`, so it takes the darkened tone — the bright
// accent is 1.8:1 here.
const ACCENT = ACCENT_ON_LIGHT;
const INTERVAL = 5000;

// Percentages, so each step sweeps the full width of the section rather than
// nudging a fixed 72px.
const slideVariants = {
  enter: (dir) => ({ x: dir > 0 ? "100%" : "-100%", opacity: 0 }),
  center: { x: 0, opacity: 1 },
  exit: (dir) => ({ x: dir > 0 ? "-100%" : "100%", opacity: 0 }),
};

const transition = { duration: 0.48, ease: [0.25, 0.46, 0.45, 0.94] };

const ServiceRotator = ({ steps, accent }) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);
  const [direction, setDirection] = useState(1);
  const [running, setRunning] = useState(false);

  // Rotate only while the section is actually on screen.
  useEffect(() => {
    const el = containerRef.current;
    if (!el) return undefined;

    const io = new IntersectionObserver(
      ([entry]) => setRunning(entry.isIntersecting),
      { threshold: 0.4 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!running) return undefined;
    const timer = setInterval(() => {
      setDirection(1);
      setActiveIndex((i) => (i + 1) % steps.length);
    }, INTERVAL);
    return () => clearInterval(timer);
  }, [running, steps.length, activeIndex]);

  const goTo = (i) => {
    setDirection(i > activeIndex ? 1 : -1);
    setActiveIndex(i);
  };

  // Arrows wrap around so the nav never dead-ends.
  const shift = (delta) => {
    setDirection(delta);
    setActiveIndex((i) => (i + delta + steps.length) % steps.length);
  };

  const step = steps[activeIndex];

  return (
    // scroll-mt clears the fixed navbar (85.5px at lg) when the footer's
    // "Why Join" link jumps to this anchor, so the heading stays visible.
    <div
      id="whyJoin"
      ref={containerRef}
      className="relative overflow-hidden pt-12 md:pt-16 pb-6 md:pb-8 scroll-mt-[72px] lg:scroll-mt-[96px]"
    >
      {/* Ambient tints */}
      <div className="absolute -top-40 -right-40 w-[700px] h-[700px] light-sphere-secondary rounded-full pointer-events-none" />
      <div className="absolute bottom-0 -left-40 w-[500px] h-[500px] light-sphere-secondary rounded-full pointer-events-none opacity-60" />

      {/* 32px below md. Wider than the 24px the card sections use, because
          this one is plain text on the page background rather than cards with
          their own edges, and at 24px the paragraph ran the full width with
          nothing to hold it in. Written as an arbitrary value, not `px-8`:
          Bootstrap ships its own spacing utilities with `!important`, which
          would beat `md:px-16` and `lg:px-24` at every width and flatten the
          desktop padding. */}
      <div className="relative z-10 w-full px-[32px] md:px-16 lg:px-24">
        {/* Heading block mirrors the Our Podcast / Mentors sections. */}
        <div className="text-center mb-12 md:mb-16">
          <h2 className="font-headline font-bold tracking-tight text-[#111113] text-[clamp(1.75rem,3.4vw,2.5rem)] leading-[1.12] m-0">
            Why join The Crypto Hub?
          </h2>
          <p className="font-headline text-[#4a4753] text-base md:text-lg leading-relaxed max-w-2xl mx-auto mt-4 mb-0">
            {SUBCOPY}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-[1fr_auto] gap-16 items-center">
          {/* Left: animated step content */}
          <div style={{ minHeight: 320 }} className="relative overflow-hidden">
            <AnimatePresence custom={direction} mode="wait">
              <motion.div
                key={activeIndex}
                custom={direction}
                variants={slideVariants}
                initial="enter"
                animate="center"
                exit="exit"
                transition={transition}
              >
                {/* Hierarchy on purpose: pill > title > description. The pill
                    keeps its own padding (a negative margin would be clipped by
                    the slide's overflow-hidden), so from md up everything under
                    it is indented by the same amount to line the text up. On a
                    phone that indent is dropped: there is no room to spare, and
                    the sections below this one run flush to the 16px gutter. */}
                <span className="font-label uppercase tracking-[0.1em] whitespace-nowrap text-black/60 text-3xl md:text-4xl inline-flex items-center rounded-full bg-black/[0.06] px-5 md:px-7 py-3 mb-6">
                  {step.label}
                </span>

                <div className="pl-0 md:pl-7">
                  <h2 className="font-headline font-extrabold text-xl md:text-2xl text-[#111113] tracking-tight leading-[1.2] mb-4">
                    {step.title}
                  </h2>

                  <p className="text-[#4a4753] text-base md:text-lg leading-relaxed mb-8 max-w-3xl">
                    {step.description}
                  </p>

                  <span
                    className="font-label text-sm uppercase tracking-[0.15em]"
                    style={{ color: accent }}
                  >
                    — {step.callout}
                  </span>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Mobile nav: the column below is `hidden md:flex`, so phones would
              otherwise have no way to change the slide. Chevrons point
              left/right here because the slide moves horizontally.
              `md:!hidden` because the legacy global `.flex { display: flex }`
              loads after Tailwind and would otherwise beat `md:hidden`. */}
          <div className="flex md:!hidden items-center !justify-center !flex-nowrap gap-5">
            <button
              type="button"
              onClick={() => shift(-1)}
              aria-label="Previous service"
              className="shrink-0 !flex items-center !justify-center w-11 h-11 rounded-full border-0 p-0 cursor-pointer active:scale-95 transition-transform"
              style={{ color: accent, backgroundColor: `${accent}14` }}
            >
              <ChevronLeft size={20} strokeWidth={2.5} />
            </button>

            <div className="flex items-center !flex-nowrap !justify-center gap-2">
              {steps.map((s, i) => (
                <motion.button
                  key={s.number}
                  onClick={() => goTo(i)}
                  aria-label={`Show ${s.label}`}
                  className="h-1.5 rounded-full border-0 p-0"
                  style={{ backgroundColor: accent }}
                  animate={{
                    width: i === activeIndex ? 28 : 6,
                    opacity: i === activeIndex ? 1 : 0.2,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              ))}
            </div>

            <button
              type="button"
              onClick={() => shift(1)}
              aria-label="Next service"
              className="shrink-0 !flex items-center !justify-center w-11 h-11 rounded-full border-0 p-0 cursor-pointer active:scale-95 transition-transform"
              style={{ color: accent, backgroundColor: `${accent}14` }}
            >
              <ChevronRight size={20} strokeWidth={2.5} />
            </button>
          </div>

          {/* Right: ghost number + dot nav */}
          <div className="hidden md:flex !flex-nowrap items-center gap-10">
            <div className="relative" style={{ minHeight: 220, minWidth: 180 }}>
              <AnimatePresence custom={direction} mode="wait">
                <motion.span
                  key={activeIndex}
                  custom={direction}
                  variants={slideVariants}
                  initial="enter"
                  animate="center"
                  exit="exit"
                  transition={{ ...transition, delay: 0.04 }}
                  className="font-headline font-extrabold leading-none tracking-tighter select-none absolute inset-0 flex items-center !justify-center text-[11rem]"
                  style={{ color: `${accent}12` }}
                >
                  {step.number}
                </motion.span>
              </AnimatePresence>
            </div>

            <div className="flex flex-col !flex-nowrap gap-3 items-center">
              <button
                type="button"
                onClick={() => shift(-1)}
                aria-label="Previous service"
                className="self-center shrink-0 !flex items-center !justify-center w-8 h-8 mb-1 rounded-full border-0 p-0 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: accent, backgroundColor: `${accent}14` }}
              >
                <ChevronUp size={18} strokeWidth={2.5} />
              </button>

              {steps.map((s, i) => (
                <motion.button
                  key={s.number}
                  onClick={() => goTo(i)}
                  aria-label={`Show ${s.label}`}
                  className="w-1 rounded-full border-0 p-0"
                  style={{ backgroundColor: accent }}
                  animate={{
                    height: i === activeIndex ? 36 : 6,
                    opacity: i === activeIndex ? 1 : 0.2,
                  }}
                  transition={{ duration: 0.3, ease: "easeOut" }}
                />
              ))}

              <button
                type="button"
                onClick={() => shift(1)}
                aria-label="Next service"
                className="self-center shrink-0 !flex items-center !justify-center w-8 h-8 mt-1 rounded-full border-0 p-0 cursor-pointer opacity-60 hover:opacity-100 transition-opacity"
                style={{ color: accent, backgroundColor: `${accent}14` }}
              >
                <ChevronDown size={18} strokeWidth={2.5} />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const WhatWeOffer = () => (
  <div id="services" className="w-full bg-white relative">
    <ServiceRotator steps={SERVICES} accent={ACCENT} />
  </div>
);

export default WhatWeOffer;
